
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import Chart from 'chart.js/auto';
import { ChartConfiguration, ChartDataset } from 'chart.js';
import { DateTime } from 'luxon';

import { sharedState } from '../../../../framework/state';

import {
    barChartOptions,
    chartBackgroundColors,
    getMonthLabelsNew,
    getTitleOptions,
    numberTooltipCallbacks,
} from '../../../service/report_service';
import { ApplicationResource } from '../../../../../common/application/enumeration/ApplicationResource';

import { formatMonthStrings } from '../../../../../common/framework/util/time';
import { getMonthlyRealisationPrice } from '../../../client/vehicle_contract_client';
import { formatCurrency } from '../../../service/helper_service';

@Component
export default class MonthlyRealisationPriceChart extends Vue {
    readonly resourceType = ApplicationResource.VEHICLE_CONTRACT;

    @Prop(Array) readonly customerIds!: string[];
    @Prop(Date) readonly lastMonth!: Date;

    shared = sharedState;

    local = {
        monthlyRealisationPriceChart: undefined as Chart | undefined,
        chartData: {} as {
            monthlyRealisationPrice: { month: string; value: number }[];
        },
        months: [] as string[],
        loading: false,
    };

    @Watch('customerIds')
    @Watch('lastMonth')
    async inputsChanged() {
        if (this.local.loading) {
            return;
        }

        await this.loadAsyncData();
        this.addMonthlyRealisationPriceChart();
    }

    private async loadAsyncData() {
        this.local.loading = true;

        const beginDate = DateTime.fromJSDate(this.lastMonth || new Date())
            .minus({ months: 11 })
            .toJSDate();
        this.local.months = formatMonthStrings(beginDate.getFullYear(), beginDate.getMonth(), 11);

        const parameters = new Map<string, any>([['customerId', this.customerIds]]);

        const specialQueryParameters = new Map<string, any>();

        if (this.local.months) {
            specialQueryParameters.set('months', this.local.months);
        }

        const customerIds = parameters.get('customerId');

        if (customerIds && Array.isArray(customerIds) && customerIds.length > 0) {
            const monthlyRealisationPrice = await getMonthlyRealisationPrice(parameters, specialQueryParameters);
            this.local.chartData = { monthlyRealisationPrice };
        }

        this.local.loading = false;
    }

    private addMonthlyRealisationPriceChart() {
        const canvasContext = (document.getElementById(
            'monthly-realisation-price-chart',
        ) as HTMLCanvasElement)!!.getContext('2d')!!;

        const chartConfiguration: ChartConfiguration = {
            type: 'bar',
            data: {
                labels: getMonthLabelsNew(this, this.local.months),
                datasets: this.chartDataSets,
            },
            options: {
                ...barChartOptions,
                plugins: {
                    ...barChartOptions.plugins,
                    title: getTitleOptions(`${this.$t(this.titleString)}`),
                    tooltip: {
                        callbacks: { ...numberTooltipCallbacks },
                    },
                },
                scales: {
                    y: {
                        type: 'linear',
                        display: true,
                        position: 'left',
                        ticks: {
                            callback: (value) => formatCurrency(value as number),
                        },
                    },
                },
            },
        };

        this.local.monthlyRealisationPriceChart?.destroy();
        this.local.monthlyRealisationPriceChart = new Chart(canvasContext, chartConfiguration);
    }

    private get titleString(): string {
        return 'title.monthlyRealisationPrice';
    }

    private static get backgroundColors(): string[] {
        return chartBackgroundColors;
    }

    private get chartDataSets(): ChartDataset[] {
        const dataSets: ChartDataset[] = [
            {
                label: `${this.$t('label.monthlyValue')}`,
                data: [],
                backgroundColor: MonthlyRealisationPriceChart.backgroundColors[0],
            },
        ];

        this.local.months.forEach((month) => {
            const chartDataToUse = this.local.chartData;

            const realisationPrice = chartDataToUse.monthlyRealisationPrice.find(
                (number: { month: string; value: number }) => number.month === month,
            )?.value;

            if (dataSets[0] && dataSets[0].data) {
                dataSets[0].data.push(realisationPrice ? realisationPrice : 0);
            }
        });

        return dataSets;
    }
}
